import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Upload,
  FileSpreadsheet,
  MapPin,
  Users,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PinPickerMap } from './PinPickerMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'upload', label: '1. Arquivo' },
  { key: 'mapping', label: '2. Mapeamento' },
  { key: 'pin', label: '3. Localização' },
  { key: 'preview', label: '4. Revisar' },
  { key: 'result', label: '5. Concluído' },
];

const FIELD_LABELS = {
  nome: 'Nome *',
  telefone: 'Telefone *',
  data_nascimento: 'Data de nascimento',
  observacoes: 'Observações',
};

export const BulkImportWizard = ({ open, onClose, onDone, primaryState = 'DF' }) => {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [uploadInfo, setUploadInfo] = useState(null); // {upload_id, columns, hints, sample_rows, total_rows}
  const [leaders, setLeaders] = useState([]);
  const [selectedLeader, setSelectedLeader] = useState('');
  const [mapping, setMapping] = useState({ nome: '', telefone: '', data_nascimento: '', observacoes: '' });
  const [pin, setPin] = useState(null); // {lat, lng}
  const [preview, setPreview] = useState(null);
  const [strategy, setStrategy] = useState('skip'); // skip | update_empty | manual
  const [manualChoices, setManualChoices] = useState({}); // {phoneNorm: 'skip'|'update'}
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // reset
    setStep('upload');
    setFile(null);
    setUploadInfo(null);
    setSelectedLeader('');
    setMapping({ nome: '', telefone: '', data_nascimento: '', observacoes: '' });
    setPin(null);
    setPreview(null);
    setStrategy('skip');
    setManualChoices({});
    setResult(null);
    axios
      .get(`${BACKEND_URL}/api/leaders`, { withCredentials: true, params: { limit: 1000 } })
      .then((r) => setLeaders(Array.isArray(r.data) ? r.data : r.data.items || []))
      .catch(() => setLeaders([]));
  }, [open]);

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const okExt = /\.(xlsx|xls|csv)$/i.test(f.name);
    if (!okExt) {
      toast.error('Formato inválido. Use .xlsx, .xls ou .csv');
      return;
    }
    setFile(f);
  };

  const analyze = async () => {
    if (!file || !selectedLeader) {
      toast.error('Selecione a liderança e o arquivo.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/supporters/bulk-import/analyze`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadInfo(data);
      setMapping({
        nome: data.hints?.nome || '',
        telefone: data.hints?.telefone || '',
        data_nascimento: data.hints?.data_nascimento || '',
        observacoes: data.hints?.observacoes || '',
      });
      setStep('mapping');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao analisar arquivo');
    } finally {
      setBusy(false);
    }
  };

  const goPreview = async () => {
    if (!mapping.nome || !mapping.telefone) {
      toast.error('Mapeie as colunas de Nome e Telefone.');
      return;
    }
    if (!pin) {
      toast.error('Marque o pin no mapa antes de prosseguir.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/supporters/bulk-import/preview`,
        { upload_id: uploadInfo.upload_id, column_mapping: mapping },
        { withCredentials: true }
      );
      setPreview(data);
      setStrategy(data.duplicates_in_db.length > 0 ? 'skip' : 'skip');
      setStep('preview');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao pré-visualizar');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/supporters/bulk-import/commit`,
        {
          upload_id: uploadInfo.upload_id,
          column_mapping: mapping,
          lideranca_id: selectedLeader,
          latitude: pin.lat,
          longitude: pin.lng,
          duplicate_strategy: strategy,
          manual_choices: strategy === 'manual' ? manualChoices : undefined,
        },
        { withCredentials: true }
      );
      setResult(data);
      setStep('result');
      onDone && onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao importar');
    } finally {
      setBusy(false);
    }
  };

  const downloadErrorReport = () => {
    if (!result || !result.errors || result.errors.length === 0) return;
    const csv = 'Linha,Motivo\n' + result.errors.map((e) => `${e.row_index},"${e.reason}"`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'erros-importacao.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentStepIdx = STEPS.findIndex((s) => s.key === step);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bulk-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Importar apoiadores em lote
          </DialogTitle>
          <DialogDescription>
            Envie uma planilha Excel/CSV e atribua os cadastros a uma liderança.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 py-2 overflow-x-auto">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s.key}>
              <div
                className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                  idx === currentStepIdx
                    ? 'bg-primary text-primary-foreground'
                    : idx < currentStepIdx
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-100 text-slate-500'
                }`}
              >
                {s.label}
              </div>
              {idx < STEPS.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="space-y-4 py-3">
            <div>
              <Label>Liderança de atribuição *</Label>
              <Select value={selectedLeader} onValueChange={setSelectedLeader}>
                <SelectTrigger data-testid="bulk-leader-select">
                  <SelectValue placeholder="Escolha a liderança dona dos apoiadores" />
                </SelectTrigger>
                <SelectContent>
                  {leaders.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.nome_completo || l.apelido} {l.apelido ? `(${l.apelido})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Os apoiadores importados contarão como redirecionamentos desta liderança no ranking.
              </p>
            </div>
            <div>
              <Label>Arquivo (.xlsx, .xls, .csv) — máx. 5.000 linhas *</Label>
              <div className="mt-1.5 rounded-lg border-2 border-dashed border-slate-300 p-6 text-center hover:border-primary transition-colors">
                <FileSpreadsheet className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFile}
                  className="hidden"
                  id="bulk-file-input"
                  data-testid="bulk-file-input"
                />
                <label htmlFor="bulk-file-input" className="cursor-pointer">
                  <span className="text-sm font-medium text-primary">
                    {file ? file.name : 'Clique para escolher um arquivo'}
                  </span>
                  {!file && <p className="text-xs text-slate-500 mt-1">ou arraste até aqui</p>}
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step: Column mapping */}
        {step === 'mapping' && uploadInfo && (
          <div className="space-y-4 py-3">
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900">
              <strong>{uploadInfo.total_rows}</strong> linhas detectadas em <strong>{uploadInfo.columns.length}</strong> colunas.
              Mapeie cada campo do sistema à coluna correspondente da planilha.
            </div>
            <div className="grid gap-3">
              {Object.entries(FIELD_LABELS).map(([field, label]) => (
                <div key={field} className="grid grid-cols-2 gap-3 items-center">
                  <Label>{label}</Label>
                  <Select
                    value={mapping[field] || 'none'}
                    onValueChange={(v) => setMapping({ ...mapping, [field]: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid={`map-${field}`}>
                      <SelectValue placeholder="— não mapear —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— não mapear —</SelectItem>
                      {uploadInfo.columns.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {/* Sample preview */}
            {uploadInfo.sample_rows.length > 0 && mapping.nome && mapping.telefone && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Amostra (3 primeiras linhas):</p>
                <div className="rounded border border-slate-200 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-2 py-1 text-left">Nome</th>
                        <th className="px-2 py-1 text-left">Telefone</th>
                        {mapping.data_nascimento && <th className="px-2 py-1 text-left">Nasc.</th>}
                        {mapping.observacoes && <th className="px-2 py-1 text-left">Obs.</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadInfo.sample_rows.slice(0, 3).map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">{r[mapping.nome] || ''}</td>
                          <td className="px-2 py-1 font-mono">{r[mapping.telefone] || ''}</td>
                          {mapping.data_nascimento && <td className="px-2 py-1">{r[mapping.data_nascimento] || ''}</td>}
                          {mapping.observacoes && <td className="px-2 py-1">{r[mapping.observacoes] || ''}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step: Pin picker */}
        {step === 'pin' && (
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
              <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <strong>Localização em massa.</strong> Clique no mapa para posicionar o pin. Cidade e bairro
                serão preenchidos automaticamente para <strong>todos os apoiadores importados</strong>.
              </div>
            </div>
            <PinPickerMap defaultState={primaryState} value={pin} onChange={setPin} height={380} />
            {pin && (
              <div className="text-xs text-slate-600 font-mono flex items-center gap-2">
                <MapPin className="h-3 w-3 text-emerald-600" />
                {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
              </div>
            )}
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && preview && (
          <div className="space-y-4 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-lg border p-3">
                <p className="text-[10px] uppercase text-slate-500">Linhas</p>
                <p className="text-2xl font-bold">{preview.total_rows}</p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-[10px] uppercase text-emerald-700">Novos</p>
                <p className="text-2xl font-bold text-emerald-800">{preview.unique_new}</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-[10px] uppercase text-amber-700">Duplicados no arquivo</p>
                <p className="text-2xl font-bold text-amber-800">{preview.duplicates_in_file}</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-[10px] uppercase text-blue-700">Já no sistema</p>
                <p className="text-2xl font-bold text-blue-800">{preview.duplicates_in_db.length}</p>
              </div>
            </div>

            {preview.invalid_rows.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <AlertTriangle className="h-4 w-4" /> {preview.invalid_rows.length} linhas inválidas serão puladas
                </div>
                <ul className="list-disc ml-5 space-y-0.5 max-h-24 overflow-y-auto">
                  {preview.invalid_rows.slice(0, 8).map((r, i) => (
                    <li key={i}>Linha {r.row_index}: {r.reason}</li>
                  ))}
                  {preview.invalid_rows.length > 8 && <li>… e mais {preview.invalid_rows.length - 8}</li>}
                </ul>
              </div>
            )}

            {preview.duplicates_in_db.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold">
                  {preview.duplicates_in_db.length} apoiadores já existem — o que fazer com eles?
                </p>
                <RadioGroup value={strategy} onValueChange={setStrategy}>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="skip" id="strategy-skip" data-testid="strategy-skip" />
                    <label htmlFor="strategy-skip" className="text-sm cursor-pointer">
                      <strong>Pular todos</strong> — mantém os cadastros existentes intactos.
                    </label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="update_empty" id="strategy-update" data-testid="strategy-update" />
                    <label htmlFor="strategy-update" className="text-sm cursor-pointer">
                      <strong>Preencher campos vazios</strong> — atualiza apenas os campos que ainda estão em branco.
                    </label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="manual" id="strategy-manual" data-testid="strategy-manual" />
                    <label htmlFor="strategy-manual" className="text-sm cursor-pointer">
                      <strong>Revisar caso a caso</strong> — escolha na tabela abaixo o que fazer com cada um.
                    </label>
                  </div>
                </RadioGroup>
                {strategy === 'manual' && (
                  <div className="rounded border border-slate-200 max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Cadastro existente</th>
                          <th className="px-2 py-1 text-left">Dado no arquivo</th>
                          <th className="px-2 py-1">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.duplicates_in_db.map((d) => {
                          const key = d.phone_normalized;
                          const choice = manualChoices[key] || 'skip';
                          return (
                            <tr key={key} className="border-t">
                              <td className="px-2 py-1">
                                <div className="font-medium">{d.existing.nome || '—'}</div>
                                <div className="text-[10px] text-slate-500">{d.existing.whatsapp}</div>
                              </td>
                              <td className="px-2 py-1">
                                <div>{d.incoming.nome}</div>
                                {d.incoming.data_nascimento && <div className="text-[10px]">Nasc: {d.incoming.data_nascimento}</div>}
                              </td>
                              <td className="px-2 py-1 text-center">
                                <Select
                                  value={choice}
                                  onValueChange={(v) => setManualChoices({ ...manualChoices, [key]: v })}
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="skip">Pular</SelectItem>
                                    <SelectItem value="update">Atualizar</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step: Result */}
        {step === 'result' && result && (
          <div className="space-y-4 py-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h3 className="text-2xl font-bold">Importação concluída!</h3>
            <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-[10px] uppercase text-emerald-700">Novos</p>
                <p className="text-2xl font-bold text-emerald-800">{result.imported}</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-[10px] uppercase text-blue-700">Atualizados</p>
                <p className="text-2xl font-bold text-blue-800">{result.updated}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] uppercase text-slate-700">Pulados</p>
                <p className="text-2xl font-bold text-slate-800">{result.skipped}</p>
              </div>
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="text-xs text-red-700 space-y-1">
                <p>{result.errors.length} linhas com erro</p>
                <Button variant="outline" size="sm" onClick={downloadErrorReport}>
                  Baixar relatório de erros
                </Button>
              </div>
            )}
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
              <Badge variant="secondary" className="font-normal">Cadastro manual</Badge>
              Os novos apoiadores foram marcados para auditoria.
            </p>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          {step !== 'result' && (
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
          )}
          {step === 'upload' && (
            <Button onClick={analyze} disabled={!file || !selectedLeader || busy} data-testid="bulk-analyze-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Analisar arquivo
            </Button>
          )}
          {step === 'mapping' && (
            <>
              <Button variant="ghost" onClick={() => setStep('upload')} disabled={busy}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
              </Button>
              <Button onClick={() => setStep('pin')} disabled={!mapping.nome || !mapping.telefone || busy}>
                <ArrowRight className="h-4 w-4 mr-2" /> Próximo: Localização
              </Button>
            </>
          )}
          {step === 'pin' && (
            <>
              <Button variant="ghost" onClick={() => setStep('mapping')} disabled={busy}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
              </Button>
              <Button onClick={goPreview} disabled={!pin || busy} data-testid="bulk-preview-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                Revisar importação
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setStep('pin')} disabled={busy}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
              </Button>
              <Button onClick={commit} disabled={busy} data-testid="bulk-commit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Users className="h-4 w-4 mr-2" />}
                Importar {preview?.unique_new || 0} apoiadores
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button onClick={onClose} data-testid="bulk-close-btn">
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
